'use client';

/**
 * ProductActions - Product-level quick actions
 *
 * Available actions:
 * - Add to cart (with quantity)
 * - Request quote for product
 * - View in Odoo
 * - Find alternatives
 *
 * @component_role Product-level Actions
 * @business_rule Product procurement actions: Add to Cart (with quantity picker),
 *   Request Quote (creates RFQ with single product), View Vendor Catalog (opens
 *   vendor's full catalog). Shows "In Cart" badge if already added.
 * @user_outcome Act on products immediately - add to cart or request a quote
 *   without navigating back to vendor catalog.
 * @integration_point cart-store, klyde_edge /rfq/create
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useState } from 'react';
import {
  ShoppingCart,
  FileText,
  ExternalLink,
  ChevronRight,
  Box,
  Minus,
  Plus,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useProductExplore } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCartStore } from '@/lib/stores/cart-store';

interface ProductActionsProps {
  nodeId: string;
  projectId?: string | null;
}

export function ProductActions({ nodeId, projectId }: ProductActionsProps) {
  const [quantity, setQuantity] = useState(1);
  const productExploreStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'product_explore'));
  const { data } = useProductExplore(productExploreStrategy!, { product_id: nodeId });
  const product = data?.product;

  const addItem = useCartStore((state) => state.addItem);

  const ODOO_HOST = process.env.NEXT_PUBLIC_ODOO_URL || 'http://klyde.vivita-ai.com';

  const handleAddToCart = () => {
    if (!product) return;

    addItem({
      product_sid: product.semantic_id,
      name: product.name,
      sku: product.sku || undefined,
      vendor_name: product.vendor_name || undefined,
      vendor_id: product.vendor_id || undefined,
      list_price: product.list_price,
      image_url: product.images?.thumbnail || product.images?.primary || undefined,
      quantity,
    });

    toast.success('Added to cart', {
      description: `${quantity}× ${product.name}`,
    });

    setQuantity(1);
  };

  const handleRequestQuote = () => {
    if (!product) return;

    // Add to cart first, then could navigate to RFQ creation
    handleAddToCart();
    toast.info('Request Quote', {
      description: 'Navigate to create RFQ with this product',
    });
  };

  return (
    <div className="space-y-4 p-3">
      {/* Header */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">PRODUCT ACTIONS</h4>
        <div className="p-2 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2">
            <Box className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium truncate">{product?.name || nodeId}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs">
            {product?.list_price && (
              <span className="font-medium text-success">${product.list_price.toFixed(0)}</span>
            )}
            {product?.vendor_name && (
              <span className="text-muted-foreground">• {product.vendor_name}</span>
            )}
          </div>
        </div>
      </div>

      {/* Add to Cart - Primary Action */}
      <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
        <div className="flex items-center gap-2 mb-3">
          <ShoppingCart className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Add to Cart</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Quantity selector */}
          <div className="flex items-center border border-border rounded-md">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="p-2 hover:bg-accent transition-colors"
              disabled={quantity <= 1}
            >
              <Minus className="h-3 w-3" />
            </button>
            <span className="px-3 text-sm font-medium min-w-[40px] text-center">{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="p-2 hover:bg-accent transition-colors"
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>

          <Button
            onClick={handleAddToCart}
            className="flex-1"
            disabled={!product}
          >
            Add {quantity > 1 ? `(${quantity})` : ''}
          </Button>
        </div>

        {product?.qty_available != null && (
          <div className="mt-2 text-xs text-muted-foreground">
            {product.qty_available > 0
              ? `${product.qty_available} in stock`
              : 'Out of stock - will be ordered'
            }
          </div>
        )}
      </div>

      {/* Other Actions */}
      <div className="space-y-2">
        <button
          onClick={handleRequestQuote}
          disabled={!product}
          className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-accent border border-transparent transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-muted">
              <FileText className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <div className="text-sm font-medium">Request Quote</div>
              <div className="text-xs text-muted-foreground">Create RFQ for this product</div>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>

        <button
          onClick={() => {
            toast.info('Find Alternatives', {
              description: 'Alternative product search coming soon',
            });
          }}
          className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-accent border border-transparent transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-muted">
              <Search className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <div className="text-sm font-medium">Find Alternatives</div>
              <div className="text-xs text-muted-foreground">Search similar products</div>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>

        <button
          onClick={() => {
            if (product?.odoo_id) {
              window.open(`${ODOO_HOST}/web#id=${product.odoo_id}&model=product.template&view_type=form`, '_blank');
            } else {
              toast.error('Cannot open', { description: 'Product ID not available' });
            }
          }}
          disabled={!product?.odoo_id}
          className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-accent border border-transparent transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-muted">
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <div className="text-sm font-medium">Open in Odoo</div>
              <div className="text-xs text-muted-foreground">View full product details</div>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}
